import { applyLoyaltySaleEffects } from '../../../loyalty/infra/loyaltyApi'
import { usePosLoyaltyStore } from '../../../loyalty/store/usePosLoyaltyStore';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';
import { reportUserVisibleFailure, captureCatch } from '../../../shared/sentry/sentryUser';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { NavigationProp } from '@react-navigation/native';
import { useShallow } from 'zustand/react/shallow';

import { useAuthStore } from '../../../auth/store/useAuthStore';
import { useBranchPermission } from '../../../shell/session/hooks/useBranchPermission'
import { useHasBusinessCapability } from '../../../shell/session/hooks/useEffectiveBusinessCapabilities'
import { useSupervisorPinPrompt } from '../../../shell/session/useSupervisorPinPrompt'
import { useSessionStore } from '../../../shell/session/store/useSessionStore'
import { authorizeSupervisorPin, mapSupervisorPinError, requireBranchSupervisorPin } from '../../../shell/session/supervisorPinApi'
import { useModulePermission, canModuleAction } from '../../../shell/session/hooks/useModulePermission'
import { usePlanEntitlements } from '../../../shell/providers/PlanEntitlementsProvider'
import { navigateInShell } from '../../../shell/navigation/rootNavigation'
import { cashManagementNavHint } from '../../../shell/navigation/cashNavigationHint';
import { showError, showErrorAfterModalClose, showInfo, showSuccessAfterModalClose } from '../../../shared/ui/appToast';
import { useOperationalConfirm } from '../../../shared/ui/useOperationalConfirm';
import { isDatabaseUnavailableError } from '../../../shared/localDb';
import { useWebPosWriteGate } from '../../application/useWebPosWriteGate';

import {
  bumpCashSessionChangeSignal,
  invalidateCashSummaryCaches,
} from '../../../cash/infra-api';
import { getStockByProductAndBranch } from '../../../inventory/stock/infra/queries/stockQueries';
import { createSale, getStockWarnings as getPosStockWarningsForSale } from '../../../sales/public-api'
import { SaleAlreadyVoidedError, formatSaleDisplayRef, saveSaleAttachment } from '../../../sales/public-api';
import { getSessionOrganizationId } from '../../../shell/session/sessionOrganization';
import { recoverPendingPosSale } from '../../application/posCheckoutSaleRecovery';
import {
  getOrganizationBusinessProfileForPos,
  isPosStaffTipsEnabled,
  resolvePosStaffTipAmount,
} from '../../application/posStaffTips';
import { formatPosMoney } from '../../application/posMoneyFormat';
import { getPosFulfillmentType, posFulfillmentHeaderTitle } from '../../../shared/pos/posHandoffSettings';
import { getLastPosPaymentMethodId, setLastPosPaymentMethodId } from '../../../shared/localPrefs';
import { NON_FISCAL_RECEIPT_DISCLAIMER } from '../../../printing/application/buildSaleReceiptPayload';
import { printSaleReceiptFromCheckout } from '../../../printing/application/printSaleTicket';
import { fetchSalePickupTrackingUrl } from '../../../printing/infra/pickupTrackingRepo';
import { shouldIncludePickupTrackingOnReceipt } from '../../../printing/application/pickupTrackingReceiptPolicy';
import {
  fiscalSalePaymentConditionLabel,
  printFiscalComprobante,
  type FiscalReceiptPrintInput,
} from '../../../printing/application/printFiscalComprobante';
import { shouldAutoPrintSaleReceipt } from '../../../printing/application/printOrchestrator';
import { autoPrintKitchenComandaIfEnabled } from '../../../printing/application/autoPrintKitchenComanda';
import { ticketPrintSuccessDetailSuffix } from '../../../printing/domain/printUserMessages';
import { getBranchReceiptPrintConfig } from '../../../printing/infra/printConfigRepo';
import { toMinorUnits } from '@shared/money';
import { formatEditableMoneyAmount } from '../../../shared/numeric/localeNumeric';
import { getPosCheckoutMoneyScale } from '../../application/posCheckoutMoneyContext';
import {
  attachKitchenTicketsToSale,
  createKitchenTicket,
  createKitchenTicketForTable,
  upsertTableSessionCart,
} from '../../../hospitality/infra/hospitalityRepo';
import { formatOptionsLabel } from '../../../catalog/domain-api'
import { getKitchenDestinationMap, syncCatalogSnapshotFromRemote } from '../../../catalog/infra-api';
import {
  FISCAL_SYSTEM_COPY,
  formatFiscalEmissionErrorForUser,
  isFiscalDataRejectionError,
  isValidCuitDigits,
  normalizeCuitDigits,
  reconcileCbteTipoAndReceptorIva,
  resolveInvoiceType,
  resolvePosSaleFiscalMode,
  type PosFiscalBuyerDraft,
  type PosFiscalDefaultStrategy,
} from '../../../fiscal/domain-api';
import { getFiscalContext, resolveFiscalReadiness, emitFiscalInvoiceAwaitingCae } from '../../../fiscal/infra-api';
import { buildFiscalPendingReceiptFooterNotes } from '../../../printing/application/buildSaleReceiptPayload';
import { scheduleCustomerMaturityNudge } from '../../../onboarding/application/maturityNudges';
import { trackOnboardingFunnel } from '../../../onboarding/application/onboardingFunnel';
import { invalidateDay1Evidence } from '../../../onboarding/application/day1PathEvidence';
import { useDay1PathNavStore } from '../../../onboarding/store/useDay1PathNavStore';
import { hapticSuccess } from '../../../shared/ui/haptics';
import { pullCustomerDataWhenNeeded } from '../../../sync/core/pullCustomerWhenNeeded';
import type { CartEvaluationResult } from '../../../promotions/public-api';
import type { CartPromoUiStatus } from '../../application/posCartPromoPresentation';
import { STALE_PROMO_MESSAGE } from '../../application/posCartPromoPresentation';

import { derivePosCheckoutTotals } from '../../application/posScreen.utils';
import {
  diffCartAgainstCatalog,
  formatPosCartCatalogDriftMessage,
  hasBlockingCartDrift,
  hasCartPriceDrift,
  loadPosCartCatalogQuotes,
} from '../../application/posCartCatalogDrift';
import { mapPosCreateSaleRequest } from '../../application/posCreateSaleRequest';
import { resolvePosDiscountPinGate } from '../../application/supervisorDiscountPolicy';
import {
  findCustomerCreditExceeded,
  findUnassignedServiceLines,
  normalizePaymentDrafts,
  pickDefaultPaymentMethodId,
  validateCheckoutDraftsBasic,
  validateCheckoutPaidVersusTotal,
} from '../../application/posCheckoutValidation';
import type { CustomerCreditExceededRow } from '../../application/posCheckoutValidation';
import {
  loadOpenCheckoutContext,
} from '../../application/posCheckoutCommands';
import type {
  PosCheckoutCustomerOption,
  PosDraftPayment,
  PosPaymentMethodId,
  PosPaymentMethodRow,
  PosStockWarningDetail,
} from '../../domain/posTypes';
import { usePosStore, type CartItem } from '../../store/usePosStore';
import { usePosCheckoutStore } from '../../store/usePosCheckoutStore';
import { usePosPriceListStore } from '../../store/usePosPriceListStore';
import { usePromotionsReloadBus } from '../../../promotions/public-api';
import { usePosCashSessionStore } from '../../store/usePosCashSessionStore';
import { supabase } from '../../../shared/supabase/supabaseClient';

type Nav = NavigationProp<Record<string, object | undefined>>;
type SaleItemsArg = Parameters<typeof getPosStockWarningsForSale>[1];

type FiscalGateResult = {
  fiscalProfile: any;
  fiscalInvoiceType: any;
  fiscalBuyer: {
    fiscalBuyerCuit: string | null;
    fiscalBuyerName: string | undefined;
    fiscalBuyerTaxCondition: number | null;
    fiscalInvoiceType: any;
  } | null;
};

function showCheckoutError(error: unknown, fallback: string, afterModalClose = false) {
  const show = afterModalClose ? showErrorAfterModalClose : showError;
  if (isDatabaseUnavailableError(error)) {
    show('Base local no disponible', 'La base local no está disponible. Reintentá.');
    return;
  }
  // sale_already_voided: la venta ya está anulada en el server. Mensaje claro y accionable
  // (cargar de nuevo la venta). Se sigue reportando vía reportUserVisibleFailure abajo.
  if (error instanceof SaleAlreadyVoidedError) {
    show('Venta anulada', error.message);
    return;
  }
  const rawMsg = (error as { message?: string })?.message ?? '';
  // Errores de negocio del command server-side (vigencia de combo, drift de
  // precios). Llegan como `code: detalle` — se muestran sin el prefijo técnico.
  if (rawMsg.includes('combo_not_available')) {
    reportUserVisibleFailure({ module: 'pos', op: 'pos.checkout', error, title: 'Combo fuera de vigencia' });
    show('Combo fuera de vigencia', 'Este combo no está vigente hoy. Revisá la vigencia en Catálogo → Combos.');
    return;
  }
  // Backstop del gate server-side: el cliente lo valida antes, pero un client
  // viejo puede llegar igual (cheque siempre tiene un "recibido de").
  if (rawMsg.includes('sale_check_requires_customer')) {
    show('Cliente requerido', 'Elegí el cliente que te entregó el cheque.');
    return;
  }
  if (rawMsg.includes('sale_price_mismatch') || rawMsg.includes('sale_total_mismatch')) {
    reportUserVisibleFailure({ module: 'pos', op: 'pos.checkout', error, title: 'El precio cambió' });
    show('El precio cambió', rawMsg.replace(/^.*mismatch:\s*/i, '') || 'Actualizá el carrito e intentá de nuevo.');
    return;
  }
  // Corte de red / timeout: el request pudo commitear en el server aunque el
  // response se haya perdido. El retry reusa el mismo saleId y verifica si la
  // venta ya existe — el cajero puede reintentar tranquilo, no duplica.
  if (/failed to fetch|network request failed|networkerror|timed? ?out|abort|offline|econn|socket|conexi[oó]n/i.test(rawMsg)) {
    reportUserVisibleFailure({ module: 'pos', op: 'pos.checkout', error, title: 'Sin conexión' });
    show(
      'Sin conexión',
      'Revisá la conexión y volvé a cobrar. Si la venta ya estaba registrada, la retomamos sin duplicarla.'
    );
    return;
  }
  // Sentry: reportar fallos del checkout (sale write path) que no son esperados.
  // saleIntake ya reporta con captureOpsFailure si el error viene del write;
  // este catch cubre errores pre-intake (fiscal context, validación, UI).
  reportUserVisibleFailure({ module: 'pos', op: 'pos.checkout', error, title: fallback });
  show('Error', (error as { message?: string })?.message ?? fallback);
}

export function usePosCheckoutTransaction(options: {
  navigation: Nav;
  currentBranchId: string | null | undefined;
  saleItems: SaleItemsArg;
  cart: CartItem[];
  cartEvaluation: CartEvaluationResult | null;
  promoUiStatus?: CartPromoUiStatus | null;
  discretionaryDiscountTotal?: number;
  appliedPromoCodes?: string[];
  selectedCashierPromotionId?: string | null;
  resolveEvaluationForCheckout?: () => CartEvaluationResult | null;
  canOpenCashManagement: boolean;
  mercadopagoQrMethodAllowed: boolean;
  customerMethodAllowed: boolean;
  checkMethodAllowed: boolean;
  allowFiscalSaleMode: boolean;
  defaultSaleFiscalMode: PosFiscalDefaultStrategy;
  saleDraftId?: string | null;
  onAfterSaleRecorded?: () => void | Promise<void>;
  allowsHospitality?: boolean;
  kitchenTicketsEnabled?: boolean;
  getDiningTableId?: () => string | null;
  getDiningTableName?: () => string | null;
  getTableSessionId?: () => string | null;
  /** Tras cobrar y cerrar sesión de mesa: limpiar picker + ruta. */
  onResetDiningTableAfterSale?: () => void;
  clearCart: () => void;
  clearPersistenceAfterSale: () => Promise<void>;
  /** Cierra hospitality.table_sessions al cobrar la cuenta de mesa. */
  onCloseTableSession?: (saleId: string) => void | Promise<void>;
  /** POS-S6: cobro parcial (dividir cuenta) — no cerrar mesa. */
  shouldKeepTableSessionOpen?: () => boolean;
  /** Restaura ítems no cobrados tras un cobro parcial. */
  onAfterPartialTableSale?: () => void | Promise<void>;
}) {
  const {
    navigation,
    currentBranchId,
    saleItems,
    cart,
    cartEvaluation,
    promoUiStatus = null,
    discretionaryDiscountTotal = 0,
    appliedPromoCodes = [],
    selectedCashierPromotionId = null,
    resolveEvaluationForCheckout,
    canOpenCashManagement,
    mercadopagoQrMethodAllowed,
    customerMethodAllowed,
    checkMethodAllowed,
    allowFiscalSaleMode,
    defaultSaleFiscalMode,
    saleDraftId,
    onAfterSaleRecorded,
    allowsHospitality = false,
    kitchenTicketsEnabled = false,
    getDiningTableId,
    getDiningTableName,
    getTableSessionId,
    onResetDiningTableAfterSale,
    clearCart,
    clearPersistenceAfterSale,
    onCloseTableSession,
    shouldKeepTableSessionOpen,
    onAfterPartialTableSale,
  } = options;
  const cartRef = useRef(cart);
  const cartEvaluationRef = useRef(cartEvaluation);
  cartRef.current = cart;
  cartEvaluationRef.current = cartEvaluation;

  // Idempotency: el saleId se reserva ANTES del primer intento y se reusa en
  // retries del mismo checkout — si el response se perdió tras el commit, el
  // retry recupera la venta por ese id (recoverPendingPosSale) o el upsert del
  // command la resuelve. Cleared on success, on void, on checkout dismiss.
  const pendingSaleIdRef = useRef<string | null>(null);

  /**
   * Descarta el saleId pendiente. Llamar cuando la venta asociada se anula fuera del
   * flujo normal (ej. cobro QR MP → cambiar medio / anular), para que el próximo cobro
   * genere un saleId nuevo en vez de reusar el anulado (lo que causaría sale_already_voided
   * o, peor, idempotent silencioso sobre una venta anulada = pérdida de la nueva venta).
   */
  const clearPendingSaleId = useCallback(() => {
    pendingSaleIdRef.current = null;
  }, []);

  // ── Store reads ──────────────────────────────────────────────
  const user = useAuthStore((s) => s.user);
  const { canEdit } = useBranchPermission('POS');
  const { chargeBlocked } = useWebPosWriteGate();

  const { paymentDrafts, saleFiscalMode, saleFiscalModeTouched, customerOptions, fiscalBuyerDraft } =
    usePosCheckoutStore(
      useShallow((s) => ({
        paymentDrafts: s.paymentDrafts,
        saleFiscalMode: s.saleFiscalMode,
        saleFiscalModeTouched: s.saleFiscalModeTouched,
        customerOptions: s.customerOptions,
        fiscalBuyerDraft: s.fiscalBuyerDraft,
      })),
    );

  const activeCashSessionId = usePosCashSessionStore((s) => s.activeCashSessionId);
  const stockWarningDetails = usePosCheckoutStore((s) => s.stockWarningDetails);

  // ── Store setters ────────────────────────────────────────────
  const setCheckoutVisible = usePosCheckoutStore((s) => s.setCheckoutVisible);
  const setCheckoutLoading = usePosCheckoutStore((s) => s.setCheckoutLoading);
  const setPaymentDrafts = usePosCheckoutStore((s) => s.setPaymentDrafts);
  const setSaleFiscalMode = usePosCheckoutStore((s) => s.setSaleFiscalMode);
  const setSaleFiscalModeTouched = usePosCheckoutStore((s) => s.setSaleFiscalModeTouched);
  const setPaymentMethods = usePosCheckoutStore((s) => s.setPaymentMethods);
  const setCustomerOptions = usePosCheckoutStore((s) => s.setCustomerOptions);
  const setCustomersTotal = usePosCheckoutStore((s) => s.setCustomersTotal);
  const setCustomerPageIndex = usePosCheckoutStore((s) => s.setCustomerPageIndex);
  const setActiveCashSessionId = usePosCashSessionStore((s) => s.setActiveCashSessionId);
  const setStockWarningResult = usePosCheckoutStore((s) => s.setStockWarningResult);
  const setCartLinePrice = usePosStore((s) => s.setCartLinePrice);
  const resetCheckout = usePosCheckoutStore((s) => s.resetCheckout);
  const showSaleCompleted = usePosCheckoutStore((s) => s.showSaleCompleted);
  const setShowQrModal = usePosCheckoutStore((s) => s.setShowQrModal);
  const setLastMpQrSaleId = usePosCheckoutStore((s) => s.setLastMpQrSaleId);
  const setLastMpQrAmount = usePosCheckoutStore((s) => s.setLastMpQrAmount);
  const setLastMpQrFiscalMode = usePosCheckoutStore((s) => s.setLastMpQrFiscalMode);

  // ── Internal state ───────────────────────────────────────────
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);
  const cancelledRef = useRef(false);
  const { confirm, ConfirmModal } = useOperationalConfirm();
  // Overlay propio para confirms que se muestran con el checkout abierto:
  // se monta dentro del Modal del checkout (PosCheckoutModal) — a nivel de
  // screen quedaría detrás del portal del Modal y sería inclickeable.
  const { confirm: confirmOverlay, ConfirmModal: CheckoutOverlayModal } = useOperationalConfirm();
  const { promptPin, PinModal: SupervisorPinModal } = useSupervisorPinPrompt();
  const canDiscount = useModulePermission('POS', 'discount');
  const { entitlements } = usePlanEntitlements();
  const isOwner = entitlements?.is_owner === true;
  const currentBranch = useSessionStore((s) =>
    currentBranchId ? s.branches.find((b) => b.id === currentBranchId) : null
  );

  // Estrategia 'auto_by_payment': el modo fiscal sigue al medio de cobro
  // (transferencia/QR/fiado → fiscal; efectivo → no fiscal), hasta que el
  // cajero lo cambie a mano en este cobro (saleFiscalModeTouched).
  useEffect(() => {
    if (!allowFiscalSaleMode || defaultSaleFiscalMode !== 'auto_by_payment') return;
    if (saleFiscalModeTouched) return;
    const next = resolvePosSaleFiscalMode(
      'auto_by_payment',
      paymentDrafts.map((d) => d.methodId)
    );
    if (next !== saleFiscalMode) {
      setSaleFiscalMode(next);
    }
  }, [
    paymentDrafts,
    saleFiscalMode,
    saleFiscalModeTouched,
    allowFiscalSaleMode,
    defaultSaleFiscalMode,
    setSaleFiscalMode,
  ]);

  // ── Fiscal gate (inlined from usePosFiscalGate) ──────────────
  const runFiscalGate = useCallback(async (): Promise<FiscalGateResult | null> => {
    if (saleFiscalMode !== 'fiscal') {
      return { fiscalProfile: null, fiscalInvoiceType: null, fiscalBuyer: null };
    }

    if (!fiscalBuyerDraft.consumidorFinal) {
      if (!isValidCuitDigits(fiscalBuyerDraft.cuit)) {
        showError('CUIT inválido', 'Ingresá un CUIT de 11 dígitos o marcá consumidor final.');
        return null;
      }
      // linkedCustomerId es opcional: se puede facturar a un CUIT sin crear cliente (cliente eventual)
    }

    const fiscalCtx =
      currentBranchId ? await getFiscalContext(currentBranchId).catch(captureCatch('pos', 'checkout-fiscal-context', null)) : null;

    const readiness = resolveFiscalReadiness(fiscalCtx, { fiscalEnabledInPlan: allowFiscalSaleMode });
    if (!readiness.canEmitLegalInvoice) {
      const ok = await confirm({
        title: 'Facturación no disponible',
        message: readiness.blockReason ?? 'Configurá la factura electrónica.',
        confirmLabel: readiness.nextAction?.label ?? 'Configurar',
      });
      if (ok) {
        navigateInShell(navigation, readiness.nextAction?.route ?? 'FiscalHub', {
          initialTab: readiness.nextAction?.tab,
        });
      }
      return null;
    }

    const fiscalProfile = fiscalCtx?.profile ?? null;
    const buyerTaxCondition = fiscalBuyerDraft.consumidorFinal ? 5 : (fiscalBuyerDraft.taxCondition ?? 5);
    let fiscalInvoiceType = resolveInvoiceType({
      emitterRegime: fiscalProfile?.taxRegime ?? 'monotributo',
      buyerTaxCondition,
    });
    ({ cbteTipo: fiscalInvoiceType } = reconcileCbteTipoAndReceptorIva(fiscalInvoiceType, buyerTaxCondition));

    const fiscalBuyer = {
      fiscalBuyerCuit: fiscalBuyerDraft.consumidorFinal ? null : normalizeCuitDigits(fiscalBuyerDraft.cuit),
      fiscalBuyerName: fiscalBuyerDraft.consumidorFinal ? 'Consumidor final' : fiscalBuyerDraft.razonSocial,
      fiscalBuyerTaxCondition: buyerTaxCondition,
      fiscalInvoiceType,
    };

    return { fiscalProfile, fiscalInvoiceType, fiscalBuyer };
  }, [
    currentBranchId,
    saleFiscalMode,
    fiscalBuyerDraft,
    allowFiscalSaleMode,
    navigation,
    confirm,
  ]);

  // ── Customer prompt (inlined from usePosCustomerPrompt) ────────────
  const promptCustomerLimitIfNeeded = useCallback(
    async (exceeded: CustomerCreditExceededRow[]): Promise<{ proceed: boolean; supervisorOverrideId?: string }> => {
      if (exceeded.length === 0) {
        return { proceed: true };
      }
      const first = exceeded[0];
      const normalizedPayments = normalizePaymentDrafts(paymentDrafts, getPosCheckoutMoneyScale());
      const customerDrafts = normalizedPayments.filter((d) => d.methodId === 'fiado' && d.customerId);
      const byCustomer = new Map<string, number>();
      for (const d of customerDrafts) {
        const customerId = String(d.customerId);
        byCustomer.set(customerId, (byCustomer.get(customerId) ?? 0) + d.amountNumber);
      }
      const prompt = `El cliente "${first.customer.name}" superaría el límite de crédito.\n\nSaldo actual: ${formatPosMoney(first.customer.balance)}\nConsumo a crédito: ${formatPosMoney(byCustomer.get(first.customer.id) ?? 0)}\nSaldo estimado: ${formatPosMoney(first.nextBalance)}\nLímite: ${formatPosMoney(first.limit)}`;

      setCheckoutVisible(false);
      const confirmed = await confirm({
        title: 'Se pasa del límite',
        message: `${prompt}\n\nPara cobrar igual, necesitás autorización de supervisor.`,
        confirmLabel: 'Autorizar con PIN',
        destructive: true,
      });
      if (!confirmed) {
        setCheckoutVisible(true);
        return { proceed: false };
      }

      try {
        if (!currentBranchId) {
          return { proceed: false };
        }
        const pin = await promptPin({ message: 'Ingresá el PIN de supervisor para autorizar el crédito.' });
        if (!pin) {
          return { proceed: false };
        }
        const supervisorOverrideId = await authorizeSupervisorPin({
          branchId: currentBranchId,
          pin,
          module: 'Fiado',
          action: 'credit_override',
          context: {
            customerId: first.customer.id,
            nextBalance: first.nextBalance,
            creditLimit: first.limit,
          },
        });
        return { proceed: true, supervisorOverrideId };
      } catch (error) {
        showError('No se pudo autorizar', mapSupervisorPinError(error instanceof Error ? error.message : String(error)));
        return { proceed: false };
      } finally {
        setCheckoutVisible(true);
      }
    },
    [paymentDrafts, setCheckoutVisible, confirm, currentBranchId, promptPin],
  );

  // ── Draft manager (inlined from usePosDraftManager) ──────────
  const updateDraft = useCallback(
    (id: string, patch: Partial<PosDraftPayment>) => {
      setPaymentDrafts((current) => current.map((item) => (item.id === id ? { ...item, ...patch } : item)));
      if (patch.customerId !== undefined) {
        const customer = usePosCheckoutStore
          .getState()
          .customerOptions.find((row) => row.id === patch.customerId);
        if (customer?.priceListId) {
          usePosPriceListStore.getState().setActivePriceListId(customer.priceListId);
        }
      }
    },
    [setPaymentDrafts],
  );

  const removeDraft = useCallback(
    (id: string) => {
      setPaymentDrafts((current) => current.filter((item) => item.id !== id));
    },
    [setPaymentDrafts],
  );

  const addSplitPayment = useCallback(
    (methodId: PosPaymentMethodId = 'transfer') => {
      setPaymentDrafts((current) => [
        ...current,
        {
          id: `payment-${Date.now()}-${current.length}`,
          methodId,
          amount: '',
        },
      ]);
    },
    [setPaymentDrafts],
  );

  // ── Stock correction (inlined from usePosStockCorrection) ────
  const handleRequestStockCorrection = useCallback(async () => {
    if (!currentBranchId) {
      showInfo('Seleccioná una sucursal', 'Elegí una sucursal para abrir inventario.');
      return;
    }

    const targetWarning = stockWarningDetails.find((detail) => detail.isBlocking) ?? stockWarningDetails[0] ?? null;
    if (!targetWarning) {
      setCheckoutVisible(false);
      navigation.navigate('Inventory');
      return;
    }

    try {
      // Aviso de variante: la celda de stock es la de la variante, no la del
      // parent. La ruta de Inventory resuelve stockRow solo por producto
      // (parent), así que pedir 'adjust' abriría el ajuste sobre la celda
      // equivocada → ir al ingreso ('add'), que enfoca el producto correcto.
      const stockRow = targetWarning.variantId
        ? null
        : await getStockByProductAndBranch(currentBranchId, targetWarning.productId);
      setCheckoutVisible(false);
      navigation.navigate('Inventory', {
        focusToken: `${Date.now()}`,
        focusProductId: targetWarning.productId,
        focusProductName: targetWarning.name,
        focusProductSku: targetWarning.sku ?? null,
        preferredAction: stockRow ? 'adjust' : 'add',
      });
    } catch (error: any) {
      presentUnexpectedFailure({ module: 'pos', op: 'error', error: error, title: 'Error', userMessage: 'No se pudo abrir inventario.' });
    }
  }, [currentBranchId, navigation, setCheckoutVisible, stockWarningDetails]);

  const maybePrintKitchenComandaAfterCreate = useCallback(
    async (ticketId: string | null, diningTableId: string | null) => {
      if (!currentBranchId) return;
      await autoPrintKitchenComandaIfEnabled({
        branchId: currentBranchId,
        ticketId,
        diningTableId,
        onError: (title, msg) => showErrorAfterModalClose(title, msg),
      });
    },
    [currentBranchId],
  );

  // ── Hospitality post-sale (inlined from usePosHospitalityPostSale) ──
  const createKitchenTicketAfterSale = useCallback(
    async (saleId: string, linesSnapshot?: Array<{
      name: string;
      qty: number;
      optionsLabel: string | null;
      lineNote: string | null;
    }>) => {
      if (!allowsHospitality || !kitchenTicketsEnabled || !currentBranchId) {
        return;
      }
      const diningTableId = getDiningTableId?.() ?? null;
      const lines =
        linesSnapshot ??
        cart
          .filter((line) => line.itemType === 'product')
          .flatMap((line) => {
            const mainLine = {
              name: (line.name ?? '').trim() || 'Producto',
              qty: line.quantity,
              optionsLabel: formatOptionsLabel(line.menuOptions) || null,
              lineNote: line.lineNote?.trim() || null,
            };
            const linkedLines = (line.menuOptions ?? [])
              .filter((o) => o.linkedProductId)
              .map((o) => ({
                name: `+ ${o.choiceName || 'Insumo'}`,
                qty: (o.qty || 1) * line.quantity,
                optionsLabel: null as string | null,
                lineNote: null as string | null,
              }));
            return [mainLine, ...linkedLines];
          });
      if (!lines.length) return;

      const persistTicket = async (kitchenLines: typeof lines) => {
        const ticketId = await createKitchenTicket({
          branchId: currentBranchId,
          saleId,
          diningTableId,
          lines: kitchenLines,
        });
        await maybePrintKitchenComandaAfterCreate(ticketId, diningTableId);
      };

      const productIds = cart
        .filter((l) => l.itemType === 'product' && l.productId)
        .map((l) => l.productId as string);
      if (productIds.length > 0 && !linesSnapshot) {
        const destMap = await getKitchenDestinationMap(productIds);
        const filtered = cart
          .filter((line) => line.itemType === 'product')
          .filter((line) => {
            const dest = line.productId ? destMap.get(line.productId) : 'kitchen';
            return dest !== 'none';
          })
          .flatMap((line) => {
            const mainLine = {
              name: (line.name ?? '').trim() || 'Producto',
              qty: line.quantity,
              optionsLabel: formatOptionsLabel(line.menuOptions) || null,
              lineNote: line.lineNote?.trim() || null,
            };
            const linkedLines = (line.menuOptions ?? [])
              .filter((o) => o.linkedProductId)
              .map((o) => ({
                name: `+ ${o.choiceName || 'Insumo'}`,
                qty: (o.qty || 1) * line.quantity,
                optionsLabel: null as string | null,
                lineNote: null as string | null,
              }));
            return [mainLine, ...linkedLines];
          });
        if (!filtered.length) return;
        try {
          await persistTicket(filtered);
        } catch (err) {
          reportUserVisibleFailure({ module: 'pos', op: 'pos.kitchen_ticket', error: err, title: 'La venta salió. La comanda no.' });
          showErrorAfterModalClose(
            'La venta salió. La comanda no.',
            err instanceof Error ? err.message : 'Reintentá desde Cocina o Ventas.',
          );
        }
        return;
      }
      try {
        await persistTicket(lines);
      } catch (err) {
        reportUserVisibleFailure({ module: 'pos', op: 'pos.kitchen_ticket', error: err, title: 'La venta salió. La comanda no.' });
        showErrorAfterModalClose(
          'La venta salió. La comanda no.',
          err instanceof Error ? err.message : 'Reintentá desde Cocina o Ventas.',
        );
      }
    },
    [
      allowsHospitality,
      cart,
      currentBranchId,
      getDiningTableId,
      kitchenTicketsEnabled,
      maybePrintKitchenComandaAfterCreate,
    ],
  );

  // ── POS-S4: ronda sin cobro (Mesas → Cocina) + fire-before-pay mostrador ──
  const counterFireEnabled = currentBranch?.kitchenFireMode === 'before_payment';
  const sendCourseToKitchen = useCallback(async () => {
    if (isSavingRef.current) return;
    if (!allowsHospitality || !kitchenTicketsEnabled || !currentBranchId) {
      showInfo('Cocina no habilitada', 'Verificá el plan y los permisos de cocina.');
      return;
    }
    const tableId = getDiningTableId?.() ?? null;
    const counterFire = !tableId && counterFireEnabled;
    if (!tableId && !counterFire) {
      showInfo('Seleccioná una mesa', 'La ronda va asociada a una mesa.');
      return;
    }
    const pendingLines = cartRef.current.filter(
      (line) => line.itemType === 'product' && !line.kitchenSent,
    );
    let kitchenLinesSource = pendingLines;
    if (counterFire && pendingLines.length) {
      const destMap = await getKitchenDestinationMap(
        pendingLines.map((l) => l.productId).filter(Boolean) as string[],
      );
      kitchenLinesSource = pendingLines.filter(
        (line) => !line.productId || destMap.get(line.productId) !== 'none',
      );
    }
    const kitchenLines = kitchenLinesSource.map((line) => ({
      name: (line.name ?? '').trim() || 'Producto',
      qty: line.quantity,
      optionsLabel: formatOptionsLabel(line.menuOptions) || null,
      lineNote: line.lineNote?.trim() || null,
      productId: line.productId ?? null,
      menuOptions: line.menuOptions?.length ? line.menuOptions : [],
    }));
    if (!kitchenLines.length) {
      showInfo('Nada nuevo para cocina', 'Lo que está en la cuenta ya salió, o sumá productos nuevos.');
      return;
    }
    isSavingRef.current = true;
    setIsSaving(true);
    try {
      let firedTicketId: string | null = null;
      if (counterFire) {
        // Fire-before-pay: comanda sin venta, payment_state 'pending'.
        // Se attachea a la venta al cobrar; si el cobro no se concreta queda
        // marcada "Sin cobrar" en Cocina y el staff la cancela.
        firedTicketId = await createKitchenTicket({
          branchId: currentBranchId,
          saleId: null,
          notes: null,
          lines: kitchenLines,
          paymentState: 'pending',
        });
        await maybePrintKitchenComandaAfterCreate(firedTicketId, null);
      } else if (tableId) {
        const ticketId = await createKitchenTicketForTable({
          branchId: currentBranchId,
          diningTableId: tableId,
          notes: null,
          lines: kitchenLines,
        });
        await maybePrintKitchenComandaAfterCreate(ticketId, tableId);
      }
      const sentIds = new Set(kitchenLinesSource.map((l) => l.lineId));
      const nextCart = cartRef.current.map((line) =>
        sentIds.has(line.lineId)
          ? { ...line, kitchenSent: true, kitchenTicketId: firedTicketId ?? line.kitchenTicketId }
          : line,
      );
      usePosStore.getState().replaceCart(nextCart);
      // POS-S4: keep cart — la cuenta se cobra después
      const sessionId = getTableSessionId?.() ?? null;
      if (sessionId) {
        void upsertTableSessionCart({
          sessionId,
          cartJson: nextCart,
        }).catch(captureCatch('pos', 'kitchen-course-table-cart-save'));
      }
      showInfo(
        counterFire ? 'Comanda enviada' : 'Ronda enviada',
        counterFire
          ? 'La comanda salió a cocina. Cobrá para habilitar la entrega.'
          : 'La comanda salió a cocina. La cuenta sigue abierta.',
      );
    } catch (err) {
      reportUserVisibleFailure({ module: 'pos', op: 'pos.kitchen_course', error: err, title: 'No se pudo enviar la ronda.' });
      showError(
        'No se pudo enviar',
        err instanceof Error ? err.message : 'Reintentá.',
      );
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [
    allowsHospitality,
    counterFireEnabled,
    currentBranchId,
    getDiningTableId,
    getTableSessionId,
    kitchenTicketsEnabled,
    maybePrintKitchenComandaAfterCreate,
  ]);

  const canSendCourseToKitchen = Boolean(
    allowsHospitality &&
      kitchenTicketsEnabled &&
      currentBranchId &&
      (getDiningTableId?.() || counterFireEnabled) &&
      cart.some((line) => line.itemType === 'product' && !line.kitchenSent),
  );

  const hasCommissions = useHasBusinessCapability('commissions');

  // ── Validation (inlined from usePosCheckoutValidation) ───────
  const validateCheckout = useCallback(async () => {
    if (!currentBranchId) {
      showError('Seleccioná una sucursal', 'Elegí una sucursal para cobrar.');
      return null;
    }

    // Block checkout if commissions are enabled and services lack attribution
    if (hasCommissions) {
      const unassignedServices = findUnassignedServiceLines(cart);
      if (unassignedServices.length > 0) {
        showError(
          'Servicios sin asignar',
          unassignedServices.length === 1
            ? `Asigná quién realiza "${unassignedServices[0].name}" antes de cobrar.`
            : `${unassignedServices.length} servicios no tienen quién los realiza. Asignalos antes de cobrar.`,
        );
        return null;
      }
    }

    // Paquetes: las sesiones quedan en la cuenta de un cliente — es obligatorio.
    if (
      cart.some((item) => item.itemType === 'package') &&
      !usePosCheckoutStore.getState().saleCustomerId
    ) {
      showError(
        'El paquete necesita cliente',
        'Elegí a quién le quedan las sesiones antes de cobrar.',
      );
      return null;
    }

    const moneyScale = getPosCheckoutMoneyScale();
    const merchandise = derivePosCheckoutTotals(cart, paymentDrafts, cartEvaluation, 0, moneyScale);
    const checkoutSnap = usePosCheckoutStore.getState();
    const staffTipsEnabled = isPosStaffTipsEnabled(
      getOrganizationBusinessProfileForPos(),
    );
    const tipAmount = resolvePosStaffTipAmount({
      tipIntentAmount: Number(checkoutSnap.tipIntent?.amount ?? 0),
      tipUiEnabled: checkoutSnap.tipUiEnabled,
      staffTipsEnabled,
    });
    const total = merchandise.total + tipAmount;
    let normalizedPayments = normalizePaymentDrafts(paymentDrafts, moneyScale);

    // Fiado: el monto del draft es la deuda del ticket completo — si el
    // total cambió (drift de precio, promo nueva, propina) el monto viejo no se
    // puede cobrar porque el vuelto solo entra con efectivo, y el cajero
    // queda confirmando para siempre (#1636). Se re-sincroniza al total
    // actual y se pide re-confirm para que vea el monto corregido.
    const singleDraft = paymentDrafts[0];
    if (paymentDrafts.length === 1 && singleDraft) {
      const draftPaid = normalizedPayments[0]?.amountNumber ?? 0;
      const paidMinor = toMinorUnits(draftPaid, moneyScale);
      const totalMinor = toMinorUnits(total, moneyScale);
      const totalMismatch = draftPaid > 0 && paidMinor !== totalMinor;
      if (totalMismatch) {
        if (singleDraft.methodId === 'fiado') {
          updateDraft(singleDraft.id, { amount: formatEditableMoneyAmount(total) });
          console.warn('[pos.checkout] fiado draft resynced to current total');
          showInfo(
            'Actualizamos el monto',
            'El total cambió. Revisá el fiado y confirmá de nuevo.',
          );
          return null;
        }
        // Transferencia / tarjeta / etc.: un solo medio suele ser el total exacto.
        // Si sumaron propina o cambió una promo, alineamos el draft y seguimos
        // en el mismo toque (sin dejar el botón mudo).
        if (
          singleDraft.methodId !== 'cash' &&
          singleDraft.methodId !== 'mercadopago_qr' &&
          paidMinor < totalMinor
        ) {
          updateDraft(singleDraft.id, { amount: formatEditableMoneyAmount(total) });
          normalizedPayments = normalizePaymentDrafts(
            usePosCheckoutStore.getState().paymentDrafts,
            moneyScale,
          );
          console.warn('[pos.checkout] payment draft raised to total (tip/promo)', singleDraft.methodId);
        } else if (
          singleDraft.methodId !== 'cash' &&
          singleDraft.methodId !== 'mercadopago_qr' &&
          paidMinor > totalMinor
        ) {
          updateDraft(singleDraft.id, { amount: formatEditableMoneyAmount(total) });
          console.warn('[pos.checkout] payment draft lowered to current total', singleDraft.methodId);
          showInfo(
            'Actualizamos el monto',
            'El total bajó. Confirmá el cobro de nuevo.',
          );
          return null;
        }
      }
    }

    const basic = validateCheckoutDraftsBasic({
      normalizedPayments,
      mercadopagoQrMethodAllowed,
      customerMethodAllowed,
      checkMethodAllowed,
      activeCashSessionId,
      saleCustomerId: usePosCheckoutStore.getState().saleCustomerId,
    });
    if (!basic.ok) {
      console.warn('[pos.checkout] abort: basic validation', basic.title, basic.message);
      showError(basic.title, basic.message);
      return null;
    }

    const exceeded = findCustomerCreditExceeded({ normalizedPayments, customerOptions });
    let customerCreditOverrideId: string | undefined;
    if (exceeded.length > 0) {
      const creditAuthorization = await promptCustomerLimitIfNeeded(exceeded);
      if (!creditAuthorization.proceed) {
        console.warn('[pos.checkout] abort: credit override not confirmed');
        return null;
      }
      customerCreditOverrideId = creditAuthorization.supervisorOverrideId;
    }

    const paidOk = validateCheckoutPaidVersusTotal({ total, normalizedPayments, moneyScale });
    if (!paidOk.ok) {
      console.warn('[pos.checkout] abort: paid-vs-total', paidOk.title, paidOk.message);
      showError(paidOk.title, paidOk.message);
      return null;
    }

    try {
      // #1636: sin timeout el sync podía colgar para siempre en redes lentas —
      // el cobro quedaba corriendo sin feedback ni forma de reintentar.
      // 60s: un force-sync frío del catálogo puede tardar legítimamente
      // (staging free-tier observado >30s); más allá de eso el cajero
      // reintenta mejor que esperar.
      const snapshot = await Promise.race([
        syncCatalogSnapshotFromRemote({ force: true }),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('catalog_sync_timeout')), 60_000),
        ),
      ]);
      if (!snapshot.applied && snapshot.probed !== true) {
        console.warn('[pos.checkout] abort: catalog sync not applied');
        showError('No pudimos chequear', 'No pudimos chequear precio y stock. Reintentá.');
        return null;
      }
      await usePosPriceListStore.getState().refreshCache();
      // Re-leer promos tras el force-sync: una promo activada/desactivada
      // mid-session no puede evaluarse con la lista stale (X-PROM-P2b).
      usePromotionsReloadBus.getState().bump();
      const quotes = await loadPosCartCatalogQuotes(
        cart,
        usePosPriceListStore.getState().resolveProductUnitPrice,
      );
      const drifts = diffCartAgainstCatalog(cart, quotes);
      if (drifts.length > 0) {
        // #1317: un combo que venció con el carrito armado no admite
        // «cobrar igual» — el server rechaza con combo_not_available. El
        // cajero tiene que volver y quitar la línea.
        const blockingDrift = hasBlockingCartDrift(drifts);
        const priceDrift = !blockingDrift && hasCartPriceDrift(drifts);
        const ok = await confirmOverlay({
          title: 'Catálogo cambió',
          message: formatPosCartCatalogDriftMessage(drifts),
          confirmLabel: blockingDrift
            ? 'Volver al carrito'
            : priceDrift
              ? 'Usar precio actual'
              : 'Cobrar igual',
          presentation: 'overlay',
        });
        if (!ok) { console.warn('[pos.checkout] abort: drift dialog cancelled'); return null; }
        if (blockingDrift) { console.warn('[pos.checkout] abort: blocking drift'); return null; }
        if (priceDrift) {
          // Pricing autoritativo: el server rechaza el precio viejo
          // (`sale_price_mismatch`), así que cobrar pasa por actualizar el
          // carrito al precio de catálogo. El checkout queda abierto con el
          // total nuevo para que el cajero lo revise y confirme el cobro.
          for (const drift of drifts) {
            if (drift.catalogPrice == null) continue;
            setCartLinePrice(drift.lineId, drift.catalogPrice);
          }
          // Un único medio en efectivo que cubría justo el total viejo se
          // lleva al total nuevo (delta aproximado si hay promos; el resto
          // es vuelto). Otros medios no se tocan: sobrepagar un QR es un
          // sobrecobro real — el cajero ajusta el pendiente. El fiado se
          // re-sincroniza al total fresco al entrar a validateCheckout (#1636).
          const onlyDraft = paymentDrafts[0];
          if (paymentDrafts.length === 1 && onlyDraft?.methodId === 'cash') {
            const paidAmount = normalizePaymentDrafts([onlyDraft], moneyScale)[0]?.amountNumber ?? 0;
            if (Math.abs(paidAmount - total) <= 0.01) {
              const priceDelta = drifts.reduce((sum, drift) => {
                if (drift.catalogPrice == null) return sum;
                const qty = cart.find((item) => item.lineId === drift.lineId)?.quantity ?? 1;
                return sum + (drift.catalogPrice - drift.cartPrice) * qty;
              }, 0);
              updateDraft(onlyDraft.id, { amount: formatEditableMoneyAmount(total + priceDelta) });
            }
          }
          console.warn('[pos.checkout] return: priceDrift applied, needs re-confirm');
          showInfo(
            'Actualizamos precios',
            'El catálogo cambió. Revisá el total y confirmá el cobro de nuevo.',
          );
          return null;
        }
      }

      const { canProceed, warnings, details } = await getPosStockWarningsForSale(currentBranchId, saleItems);
      setStockWarningResult(warnings, details, canProceed);
      if (!canProceed) {
        console.warn('[pos.checkout] abort: stock insuficiente');
        showError('Stock insuficiente', 'Reponé en Stock o usá «Ir a Stock» en el cobro.');
        return null;
      }
      return { forceAllowSale: warnings.length > 0, supervisorOverrideId: customerCreditOverrideId };
    } catch (error: unknown) {
      console.warn('[pos.checkout] abort: exception', error);
      reportUserVisibleFailure({ module: 'pos', op: 'pos.stock_warnings', error, title: 'No se pudo registrar la venta.' });
      showError('No pudimos chequear', 'No pudimos chequear precio y stock. Reintentá.');
      return null;
    }
  }, [
    activeCashSessionId,
    cart,
    currentBranchId,
    customerOptions,
    paymentDrafts,
    promptCustomerLimitIfNeeded,
    saleItems,
    setStockWarningResult,
    mercadopagoQrMethodAllowed,
    customerMethodAllowed,
    checkMethodAllowed,
    cartEvaluation,
    confirm,
    confirmOverlay,
    hasCommissions,
    setCartLinePrice,
    updateDraft,
  ]);

  // ── Sale submission (inlined from usePosSaleSubmission) ──────
  const runCreateSale = useCallback(
    async (forceAllowSale: boolean, customerCreditOverrideId?: string) => {
      if (chargeBlocked) {
        console.warn('[pos.checkout] abort: chargeBlocked (offline)');
        showInfo('Sin conexión', 'Cuando vuelva internet, cobrá con el mismo botón. El carrito queda.');
        return;
      }
      if (promoUiStatus?.hasStaleSelection) {
        console.warn('[pos.checkout] abort: stale promo selection');
        showInfo('Promoción', promoUiStatus.staleMessage ?? STALE_PROMO_MESSAGE);
        return;
      }
      if (!currentBranchId) {
        console.warn('[pos.checkout] abort: no currentBranchId');
        return;
      }

      const moneyScale = getPosCheckoutMoneyScale();
      const merchandiseTotals = derivePosCheckoutTotals(
        cart,
        paymentDrafts,
        cartEvaluation,
        0,
        moneyScale,
      );
      const checkoutSnapForTotal = usePosCheckoutStore.getState();
      const staffTipsEnabledForTotal = isPosStaffTipsEnabled(
        getOrganizationBusinessProfileForPos(),
      );
      const tipForCharge = resolvePosStaffTipAmount({
        tipIntentAmount: Number(checkoutSnapForTotal.tipIntent?.amount ?? 0),
        tipUiEnabled: checkoutSnapForTotal.tipUiEnabled,
        staffTipsEnabled: staffTipsEnabledForTotal,
      });
      const total = merchandiseTotals.total + tipForCharge;
      // Snapshot before any await / clearCart so cocina always gets product names.
      // CAT-S1: filter out products with kitchen_destination = 'none' (no ticket).
      const productLinesSnapshot = cart.filter((line) => line.itemType === 'product');
      const productIds = productLinesSnapshot.map((l) => l.productId).filter(Boolean) as string[];
      const kitchenDestMap = await getKitchenDestinationMap(productIds);
      // No re-crear comanda al cobrar por lo ya mandado (kitchenSent). Solo ítems nuevos.
      // Aplica a rondas de mesa y a fire-before-pay de mostrador.
      const kitchenLinesSnapshot = productLinesSnapshot
        .filter((line) => {
          const dest = line.productId ? kitchenDestMap.get(line.productId) : 'kitchen';
          if (dest === 'none') return false;
          if (line.kitchenSent) return false;
          return true;
        })
        .map((line) => ({
          name: (line.name ?? '').trim() || 'Producto',
          qty: line.quantity,
          optionsLabel: formatOptionsLabel(line.menuOptions) || null,
          lineNote: line.lineNote?.trim() || null,
          productId: line.productId ?? null,
          menuOptions: line.menuOptions?.length ? line.menuOptions : [],
        }));

      try {
        setIsSaving(true);

        const fiscalResult = await runFiscalGate();
        if (!fiscalResult) {
          console.warn('[pos.checkout] abort: fiscal gate returned null');
          return;
        }

        const freshCanDiscount = canModuleAction(
          useSessionStore.getState().branches,
          useSessionStore.getState().currentBranchId ?? undefined,
          'POS',
          'discount'
        );

        let supervisorOverrideId: string | undefined;
        const { subtotal: checkoutSubtotal } = derivePosCheckoutTotals(
          cart,
          paymentDrafts,
          cartEvaluation,
          0,
          moneyScale,
        );
        const maxPct = currentBranch?.manualDiscountMaxPercent ?? 20;
        const maxAmt = currentBranch?.manualDiscountMaxAmount ?? 5000;
        const { gate, overLimit } = resolvePosDiscountPinGate({
          isOwner,
          canDiscount: freshCanDiscount,
          discretionaryDiscountTotal,
          subtotal: checkoutSubtotal,
          maxPercent: maxPct,
          maxAmount: maxAmt,
        });
        if (gate === 'require_pin' && currentBranchId) {
          try {
            await requireBranchSupervisorPin(currentBranchId);
          } catch (error: unknown) {
            presentUnexpectedFailure({ module: 'pos', op: 'hace-falta-autorizaci-n', error: error, title: 'Hace falta autorización', userMessage: 'Esta acción la tiene que hacer alguien con permiso, o cargá un PIN en Reglas del POS.' });
            return;
          }
          const pin = await promptPin({
            message: overLimit
              ? 'El descuento manual se pasa del tope de la sucursal. Autorizá con el PIN de supervisor.'
              : 'Para aplicar este descuento hace falta autorización. Ingresá el PIN de supervisor.',
          });
          if (!pin) { console.warn('[pos.checkout] abort: supervisor pin cancelled'); return; }
          supervisorOverrideId = await authorizeSupervisorPin({
            branchId: currentBranchId,
            pin,
            module: 'POS',
            action: 'discount',
            context: {
              discountTotal: discretionaryDiscountTotal,
              subtotal: checkoutSubtotal,
              overLimit,
            },
          });
        }

        
        const loyalty = usePosLoyaltyStore.getState();
        // Cap al total post-promo: canjear de más quema puntos de más y
        // persiste discount_total > subtotal (module-audit X-LOY-P2).
        const postPromoRemaining = Math.max(
          0,
          checkoutSubtotal - (cartEvaluation?.discountTotal ?? 0),
        );
        const loyaltyDiscount = Math.min(loyalty.redeemArsDiscount(), postPromoRemaining);
        const loyaltyExtraDiscounts =
          loyaltyDiscount > 0
            ? [{ label: 'Canje puntos', discountAmount: loyaltyDiscount, scope: 'manual' as const, metadata: { loyaltyPoints: loyalty.redeemPoints } }]
            : [];
        const promoIntent =
          appliedPromoCodes.length > 0 || selectedCashierPromotionId
            ? {
                promoCodes: appliedPromoCodes,
                selectedPromotionIds: selectedCashierPromotionId
                  ? [selectedCashierPromotionId]
                  : [],
              }
            : undefined;
        const supervisorOverrides =
          supervisorOverrideId || customerCreditOverrideId
            ? {
                ...(supervisorOverrideId ? { discount: supervisorOverrideId } : {}),
                ...(customerCreditOverrideId ? { credit: customerCreditOverrideId } : {}),
              }
            : undefined;
        const checkoutSnap = usePosCheckoutStore.getState();
        const staffTipsEnabled = isPosStaffTipsEnabled(
          getOrganizationBusinessProfileForPos(),
        );
        const tipAmount = resolvePosStaffTipAmount({
          tipIntentAmount: Number(checkoutSnap.tipIntent?.amount ?? 0),
          tipUiEnabled: checkoutSnap.tipUiEnabled,
          staffTipsEnabled,
        });
        const tipUiEnabled = staffTipsEnabled && checkoutSnap.tipUiEnabled;
        const primaryStaffUserId = checkoutSnap.primaryStaffUserId;
        // Reservar el saleId ANTES del intento: si el request commitea en el
        // server pero el response se pierde (corte de red), el retry reenvía
        // el mismo id y el upsert + command idempotente resuelven sin duplicar
        // venta ni factura. Antes se generaba dentro de createSale y se perdía.
        const reservedSaleId = pendingSaleIdRef.current ?? crypto.randomUUID();
        const isRetryOfPendingSale = pendingSaleIdRef.current != null;
        pendingSaleIdRef.current = reservedSaleId;
        // Retry determinístico: si ya había un saleId reservado, el intento
        // anterior pudo commitear. Consultamos ese id exacto — si la venta
        // existe, retomamos el flujo con sus datos (counterNumber incluido)
        // sin llamar createSale. Si no existe (o la consulta falla), el
        // createSale con el mismo id lo resuelve vía upsert idempotente.
        const recoveredSale = isRetryOfPendingSale
          ? await recoverPendingPosSale(reservedSaleId)
          : null;
        const evaluationForSale =
          resolveEvaluationForCheckout?.() ?? cartEvaluationRef.current ?? cartEvaluation;
        const { input, options: saleOptions } = mapPosCreateSaleRequest({
          branchId: currentBranchId,
          cart,
          paymentDrafts,
          moneyScale,
          saleFiscalMode,
          priceListId: usePosPriceListStore.getState().activePriceListId,
          activeCashSessionId,
          cashierUserId: user?.id ?? null,
          forceAllowSale,
          cartEvaluation: evaluationForSale,
          fiscalBuyer: fiscalResult.fiscalBuyer,
          supervisorOverrideId,
          supervisorOverrides,
          diningTableId: getDiningTableId?.() ?? null,
          extraDiscounts: loyaltyExtraDiscounts,
          extraDiscountTotal: loyaltyDiscount,
          saleCustomerId: usePosCheckoutStore.getState().saleCustomerId,
          // Handoff QSR solo con kitchen_tickets o mesa: sin la capability la
          // venta es mostrador retail y no lleva fulfillment/nombre de retiro.
          pickupName: kitchenTicketsEnabled
            ? usePosCheckoutStore.getState().pickupName
            : null,
          fulfillmentType:
            kitchenTicketsEnabled || getDiningTableId?.()
              ? getPosFulfillmentType()
              : undefined,
          saleDraftId: saleDraftId ?? null,
          saleId: reservedSaleId,
          promoIntent,
          tipAmount,
          primaryStaffUserId,
        });
        const saleResult = recoveredSale
          ? {
              saleId: reservedSaleId,
              counterNumber: recoveredSale.counterNumber ?? null,
              pickupName: recoveredSale.pickupName ?? null,
              fulfillmentType: recoveredSale.fulfillmentType ?? null,
            }
          : (console.warn('[pos.checkout] invoking createSale RPC'), await createSale(input, saleOptions));
        const saleId = saleResult.saleId;
        if (loyalty.customerId) {
          const paidForLoyalty = Math.max(0, (input.total ?? 0) - tipAmount);
          const redeemAttempted = loyalty.redeemPoints > 0;
          // Dual-path expand-then-contract: server atomic path (ADR-035) ya debitó;
          // este F&F es no-op idempotente. Si el backend es viejo, cubre earn/redeem.
          void applyLoyaltySaleEffects({
            customerId: loyalty.customerId,
            saleId: String(saleId),
            paidAmount: paidForLoyalty,
            redeemPoints: loyalty.redeemPoints,
          })
            .catch((loyaltyErr) => {
              console.warn('[POS] Venta confirmada pero fallaron efectos de fidelización.', loyaltyErr);
              if (redeemAttempted) {
                showError(
                  'Venta confirmada',
                  'No pudimos sincronizar los puntos. Si el saldo no cuadra, reintentá desde Fidelización.',
                );
              }
            })
            .finally(() => usePosLoyaltyStore.getState().clear());
        } else {
          usePosLoyaltyStore.getState().clear();
        }
        if (cancelledRef.current) {
          pendingSaleIdRef.current = null;
          showSuccessAfterModalClose(
            'Venta registrada',
            `${formatPosMoney(total)} · La venta quedó en Ventas.`,
            12000,
          );
          return;
        }
        const receiptCartSnapshot = [...cart];
        const receiptPaymentsSnapshot = [...paymentDrafts];
        const receiptEvaluationSnapshot = cartEvaluation;
        // Comprobantes elegidos en checkout — snapshot antes de resetCheckout.
        const pendingAttachmentsSnapshot = usePosCheckoutStore.getState().pendingAttachments;
        if (activeCashSessionId && currentBranchId) {
          invalidateCashSummaryCaches(currentBranchId);
          bumpCashSessionChangeSignal();
        }
        try {
          const { takePendingAppointmentCheckout } = await import(
            '../../../appointments/application/appointmentPosBridge'
          );
          const { completeAppointmentSale } = await import(
            '../../../appointments/infra/appointmentsRepo'
          );
          const pendingAppt = takePendingAppointmentCheckout();
          // Only close the turn if the cart still includes every booked service.
          if (
            pendingAppt &&
            pendingAppt.productIds.every((pid) => cart.some((line) => line.productId === pid))
          ) {
            await completeAppointmentSale(pendingAppt.appointmentId, saleId, total);
          }
        } catch (linkErr) {
          reportUserVisibleFailure({ module: 'pos', op: 'pos.appointment_link', error: linkErr, title: 'La venta salió. El turno no se cerró.' });
          console.warn('[POS] appointment sale link failed', linkErr);
          showErrorAfterModalClose(
            'La venta salió. El turno no se cerró.',
            linkErr instanceof Error
              ? linkErr.message
              : 'La venta quedó registrada. Cerrá el turno desde Agenda.',
          );
        }
        // Link work order → sale + mark parts as deducted (H-21, H-22)
        try {
          const { takePendingWorkOrderCheckout } = await import(
            '../../../work-orders/application/workOrderPosBridge'
          );
          const {
            recordWorkOrderPayment,
            markWorkOrderPartsDeducted,
            linkWorkOrderToInvoice,
            linkWorkOrderToSale,
          } = await import('../../../work-orders/infra/workOrdersRepo');
          const pendingWO = takePendingWorkOrderCheckout();
          if (pendingWO) {
            const payAmount = pendingWO.amountDue;
            if (payAmount != null && payAmount > 0) {
              await recordWorkOrderPayment({
                workOrderId: pendingWO.workOrderId,
                saleId,
                amount: payAmount,
                kind: pendingWO.kind,
              });
            } else {
              await linkWorkOrderToSale(pendingWO.workOrderId, saleId);
            }
            if (pendingWO.kind !== 'deposit') {
              await markWorkOrderPartsDeducted(pendingWO.workOrderId);
            }
            // H-24: link invoice if fiscal (async — invoice is created by Edge Function post-sync)
            if (saleFiscalMode !== 'non_fiscal') {
              try {
                setTimeout(async () => {
                  try {
                    const { data: inv } = await supabase
                      .schema('pos')
                      .from('invoices')
                      .select('id')
                      .eq('sale_id', saleId)
                      .maybeSingle();
                    if (inv?.id) {
                      await linkWorkOrderToInvoice(pendingWO.workOrderId, String(inv.id));
                    }
                  } catch (invErr) {
                    console.warn('[POS] WO invoice link retry failed', invErr);
                  }
                }, 5000);
              } catch (invLinkErr) {
                console.warn('[POS] WO invoice link setup failed', invLinkErr);
              }
            }
          }
        } catch (woLinkErr) {
          reportUserVisibleFailure({ module: 'pos', op: 'pos.work_order_link', error: woLinkErr, title: 'La venta salió. La OT no se vinculó.' });
          console.warn('[POS] work order sale link failed', woLinkErr);
          showErrorAfterModalClose(
            'La venta salió. La OT no se vinculó.',
            woLinkErr instanceof Error
              ? woLinkErr.message
              : 'La venta quedó registrada. Vinculá la OT manualmente.',
          );
        }
        const normalizedPayments = normalizePaymentDrafts(paymentDrafts, moneyScale);
        const hasMpQr = normalizedPayments.some((d) => d.methodId === 'mercadopago_qr');
        const tableId = getDiningTableId?.();
        let tableKitchenTicketsAttached = 0;
        // Split parcial: solo se attachean/pagan las comandas de los ítems cobrados;
        // las del resto quedan abiertas para el cobro posterior.
        const keepSessionOpenPartial = Boolean(shouldKeepTableSessionOpen?.());
        const soldTicketIds = keepSessionOpenPartial
          ? cart.map((l) => l.kitchenTicketId).filter((id): id is string => Boolean(id))
          : [];
        const ticketAttachArgs = keepSessionOpenPartial
          ? (soldTicketIds.length ? { saleId, ticketIds: soldTicketIds } : null)
          : (tableId ? { tableId, saleId } : null);
        if (ticketAttachArgs) {
          try {
            const attachResult = await attachKitchenTicketsToSale(ticketAttachArgs);
            tableKitchenTicketsAttached = attachResult.attached;
          } catch (err) {
            reportUserVisibleFailure({ module: 'pos', op: 'pos.kitchen_tickets_attach', error: err, title: 'La venta salió. Las rondas anteriores no se vincularon.' });
            showErrorAfterModalClose(
              'La venta salió. Las rondas anteriores no se vincularon.',
              err instanceof Error ? err.message : 'Reintentá desde Ventas o Cocina.',
            );
          }
          try {
            if (!keepSessionOpenPartial) {
              await onCloseTableSession?.(saleId);
            }
          } catch (err) {
            console.warn('[POS] close table session failed', err);
          }
        }
        // Fire-before-pay: comandas de mostrador disparadas antes de cobrar se
        // attachean a la venta y pasan payment_state pending → paid.
        const firedTicketIds = [
          ...new Set(
            cart
              .map((l) => l.kitchenTicketId)
              .filter((id): id is string => Boolean(id)),
          ),
        ];
        if (firedTicketIds.length) {
          try {
            // Ítems sumados después del último fire → comanda extra (ya paga).
            let extraTicketId: string | null = null;
            if (kitchenLinesSnapshot.length) {
              extraTicketId = await createKitchenTicket({
                branchId: currentBranchId,
                saleId: null,
                lines: kitchenLinesSnapshot,
              });
              await maybePrintKitchenComandaAfterCreate(extraTicketId, null);
            }
            await attachKitchenTicketsToSale({
              saleId,
              ticketIds: extraTicketId ? [...firedTicketIds, extraTicketId] : firedTicketIds,
            });
          } catch (err) {
            reportUserVisibleFailure({ module: 'pos', op: 'pos.kitchen_tickets_attach', error: err, title: 'La venta salió. La comanda no se vinculó.' });
            showErrorAfterModalClose(
              'La venta salió. La comanda no se vinculó.',
              err instanceof Error ? err.message : 'Reintentá desde Ventas o Cocina.',
            );
          }
        } else if (tableId && tableKitchenTicketsAttached > 0) {
          // Mesa: comandas abiertas ya vinculadas — no crear ni reimprimir al cobrar.
        } else {
          await createKitchenTicketAfterSale(saleId, kitchenLinesSnapshot);
        }
        // Accrual de comisiones: server-authoritative en process_sale_command
        // (pos.apply_sale_commission_strict). No hay segundo path client-side.
        // Comprobantes (cheque/transferencia): upload best-effort post-venta —
        // la venta ya quedó registrada, un fallo acá no la invalida (regla 4.1).
        if (pendingAttachmentsSnapshot.length > 0) {
          const attachOrgId = await getSessionOrganizationId().catch(() => null);
          let attachFailed = 0;
          for (const file of pendingAttachmentsSnapshot) {
            try {
              if (!attachOrgId) throw new Error('sin org');
              await saveSaleAttachment({
                attachmentId: crypto.randomUUID(),
                saleId,
                orgId: attachOrgId,
                fileName: file.fileName,
                mimeType: file.mimeType,
                fileSize: file.fileSize,
                fileBase64: file.base64,
                createdBy: user?.id ?? null,
              });
            } catch (attachError) {
              attachFailed += 1;
              console.warn('[POS] sale attachment upload failed', attachError);
            }
          }
          if (attachFailed > 0) {
            showErrorAfterModalClose(
              'Comprobante',
              attachFailed === 1
                ? 'La venta se registró pero no pude adjuntar el comprobante. Podés adjuntarlo desde el detalle de la venta.'
                : `La venta se registró pero no pude adjuntar ${attachFailed} comprobantes. Podés adjuntarlos desde el detalle de la venta.`,
            );
          }
        }
        // Primer hito día 1 (proto aprobado, opción B): el modal queda abierto
        // con estado de éxito en lugar del toast. Ventas 2+ → toast como hoy.
        // QR: el modal de QR es su propia confirmación — igual marcamos hito.
        const firstSalePending =
          useDay1PathNavStore.getState().phase === 'need_first_sale';
        if (firstSalePending) {
          invalidateDay1Evidence(currentBranchId);
          hapticSuccess();
          trackOnboardingFunnel('checkout_completed', { milestone: 'sale' });
          // El nav store solo lo re-deriva useDay1Path (Home). Sin este avance,
          // las ventas siguientes en la misma sesión POS seguirían viendo
          // need_first_sale y repetirían el modal de éxito en vez del toast.
          const nav = useDay1PathNavStore.getState();
          nav.setFromPath({
            mode: nav.mode,
            phase: 'celebrate_first_sale',
            preferCatalogTab: nav.preferCatalogTab,
            collapseStorefrontNav: nav.collapseStorefrontNav,
          });
        }
        if (hasMpQr) {
          const mpQrDraft = normalizedPayments.find((d) => d.methodId === 'mercadopago_qr');
          const mpQrAmountRaw = mpQrDraft?.amount != null ? Number(mpQrDraft.amount) : null;
          setLastMpQrSaleId(saleId);
          setLastMpQrAmount(mpQrAmountRaw != null && Number.isFinite(mpQrAmountRaw) ? mpQrAmountRaw : null);
          setLastMpQrFiscalMode(saleFiscalMode);
          setShowQrModal(true);
          setCheckoutVisible(false);
        } else {
          // Capturar una vez: onAfterPartialTableSale limpia splitRemainingRef y una
          // segunda consulta devolvería false con ítems restantes.
          const keepTableSessionOpen = Boolean(shouldKeepTableSessionOpen?.());
          if (keepTableSessionOpen) {
            await onAfterPartialTableSale?.();
          } else {
            clearCart();
          }
          pendingSaleIdRef.current = null;
          if (firstSalePending) {
            const receivedTotal = normalizedPayments
              .filter((d) => d.methodId !== 'fiado')
              .reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
            const methodLabel =
              normalizedPayments.length > 1
                ? 'Pago combinado'
                : (usePosCheckoutStore
                    .getState()
                    .paymentMethods.find((m) => m.id === normalizedPayments[0]?.methodId)?.name ??
                  '');
            showSaleCompleted({
              total,
              received: receivedTotal,
              change: Math.max(0, receivedTotal - total),
              methodLabel,
            });
          } else {
            resetCheckout();
          }
          if (!keepTableSessionOpen) {
            onResetDiningTableAfterSale?.();
          }
          void Promise.resolve(onAfterSaleRecorded?.()).catch((err) => {
            console.warn('[POS] post-sale cart persistence cleanup', err);
            showInfo(
              'La venta salió, pero algo falló al refrescar',
              err instanceof Error
                ? err.message
                : 'Recargá el catálogo o reabrí el POS para verlo actualizado.'
            );
          });
          let saleTrackingUrl: string | null = null;
          if (
            shouldIncludePickupTrackingOnReceipt({
              diningTableId: tableId,
              diningTableName: getDiningTableName?.() ?? null,
            })
          ) {
            try {
              saleTrackingUrl = await fetchSalePickupTrackingUrl(saleId);
            } catch {
              saleTrackingUrl = null;
            }
          }
          // El Toast vive dentro del Modal Cobrar; hay que diferir o se traga al cerrar.
          const diningTableNameForPrint = getDiningTableName?.()?.trim() || null;
          const receiptArgs = {
            saleId,
            branchId: currentBranchId,
            cart: receiptCartSnapshot,
            paymentDrafts: receiptPaymentsSnapshot,
            cartEvaluation: receiptEvaluationSnapshot,
            tipAmount: tipUiEnabled ? tipAmount : 0,
            counterNumber: saleResult.counterNumber,
            pickupName: saleResult.pickupName ?? usePosCheckoutStore.getState().pickupName,
            fulfillmentType: saleResult.fulfillmentType ?? getPosFulfillmentType(),
            kitchenTickets: kitchenTicketsEnabled,
            trackingUrl: saleTrackingUrl,
            diningTableName: diningTableNameForPrint,
          };
          // Venta fiscal: esperamos el CAE (la EF es idempotente — si la venta ya
          // tiene comprobante aprobado devuelve el existente) para imprimir la
          // Factura A/B/C. Si ARCA no responde a tiempo, sale el ticket interno
          // con leyenda y el comprobante se reimprime desde Ventas.
          let fiscalPrintInput: FiscalReceiptPrintInput | null = null;
          let fiscalEmitOutcome: Awaited<ReturnType<typeof emitFiscalInvoiceAwaitingCae>> | null =
            null;
          if (saleFiscalMode === 'fiscal') {
            showInfo(FISCAL_SYSTEM_COPY.checkout.emittingTitle, FISCAL_SYSTEM_COPY.checkout.emittingBody);
            fiscalEmitOutcome = await emitFiscalInvoiceAwaitingCae(saleId);
            const emission = fiscalEmitOutcome.emission;
            if (emission?.cae?.trim()) {
              const emitterSrc = emission.emitter ?? null;
              const profile = fiscalResult.fiscalProfile;
              fiscalPrintInput = {
                invoiceType: emission.invoiceType ?? fiscalResult.fiscalBuyer?.fiscalInvoiceType,
                pointOfSale: emission.pointOfSale,
                invoiceNumber: emission.invoiceNumber,
                cae: emission.cae,
                caeExpiration: emission.caeExpiration,
                issuedAt: emission.issuedAt,
                qrUrl: emission.qrUrl,
                buyerName: emission.buyerName ?? fiscalResult.fiscalBuyer?.fiscalBuyerName,
                buyerCuit: emission.buyerCuit ?? fiscalResult.fiscalBuyer?.fiscalBuyerCuit,
                buyerTaxCondition:
                  emission.buyerTaxCondition ?? fiscalResult.fiscalBuyer?.fiscalBuyerTaxCondition,
                items: receiptCartSnapshot.map((item) => ({
                  label: item.name,
                  quantity: item.quantity,
                  price: item.price,
                })),
                tipAmount: tipUiEnabled ? tipAmount : 0,
                total: emission.totalAmount ?? input.total ?? total,
                netAmount: emission.netAmount,
                ivaAmount: emission.ivaAmount,
                saleRef: formatSaleDisplayRef({
                  saleId,
                  counterNumber: saleResult.counterNumber,
                }),
                diningTableName: diningTableNameForPrint,
                copyLabel: 'ORIGINAL',
                emitter: {
                  businessName: emitterSrc?.businessName ?? profile?.businessName,
                  cuit: emitterSrc?.cuit ?? profile?.emitterCuit,
                  taxRegime: emitterSrc?.taxRegime ?? profile?.taxRegime,
                  domicilioComercial:
                    emitterSrc?.domicilioComercial ?? profile?.domicilioComercial,
                  iibb: emitterSrc?.iibb ?? profile?.iibb,
                  inicioActividades:
                    emitterSrc?.inicioActividades ?? profile?.inicioActividades,
                },
                environment:
                  emitterSrc?.environment ?? profile?.environment ?? null,
                salePaymentConditionLabel: fiscalSalePaymentConditionLabel(
                  normalizedPayments.map((d) => d.methodId),
                ),
                trackingUrl: saleTrackingUrl,
                fulfillmentLabel: kitchenTicketsEnabled
                  ? posFulfillmentHeaderTitle(
                      (saleResult.fulfillmentType ??
                        getPosFulfillmentType()) as 'pickup' | 'dine_in',
                    )
                  : null,
              };
            }
          }
          const fiadoPendingAmount = normalizedPayments
            .filter((d) => d.methodId === 'fiado')
            .reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
          const footerNotes =
            saleFiscalMode === 'fiscal'
              ? fiscalPrintInput
                ? undefined
                : buildFiscalPendingReceiptFooterNotes({
                    accountPendingAmount: fiadoPendingAmount > 0 ? fiadoPendingAmount : undefined,
                  })
              : allowFiscalSaleMode
                ? [NON_FISCAL_RECEIPT_DISCLAIMER]
                : undefined;
          const approvedFiscalInput = fiscalPrintInput;
          const fiscalAwaitingCae =
            saleFiscalMode === 'fiscal' && !approvedFiscalInput;
          const fiscalEmitLastError = fiscalAwaitingCae ? fiscalEmitOutcome?.lastError ?? null : null;
          const [autoPrintEnabled, receiptPrintConfig] = await Promise.all([
            shouldAutoPrintSaleReceipt(currentBranchId),
            getBranchReceiptPrintConfig(currentBranchId),
          ]);
          const printSaleDoc = (autoDialog: boolean) =>
            approvedFiscalInput
              ? () =>
                  printFiscalComprobante({
                    branchId: currentBranchId,
                    input: approvedFiscalInput,
                  }).catch((err) => console.warn('[POS] fiscal print failed', err))
              : () =>
                  printSaleReceiptFromCheckout({
                    ...receiptArgs,
                    footerNotes,
                    autoOpenPrintDialog: autoDialog,
                  }).catch((err) => console.warn('[POS] receipt print failed', err));
          const toastDetail = fiscalPrintInput
            ? autoPrintEnabled
              ? ' · Factura enviada a imprimir'
              : ' · Imprimí la factura o hacelo después desde Ventas'
            : saleFiscalMode === 'fiscal'
              ? FISCAL_SYSTEM_COPY.checkout.saleRegisteredWithFiscalPending
              : autoPrintEnabled
                ? ticketPrintSuccessDetailSuffix(receiptPrintConfig.mode)
                : ' · Imprimir ticket o hacerlo después desde Ventas';
          if (!firstSalePending) {
            showSuccessAfterModalClose(
              'Venta registrada',
              `${formatPosMoney(total)}${toastDetail}`,
              autoPrintEnabled ? 7000 : 12000,
              autoPrintEnabled ? undefined : () => void printSaleDoc(false)(),
            );
          }
          if (fiscalAwaitingCae) {
            const rejected = isFiscalDataRejectionError(fiscalEmitLastError);
            showErrorAfterModalClose(
              rejected ? FISCAL_SYSTEM_COPY.checkout.rejectedTitle : FISCAL_SYSTEM_COPY.checkout.pendingTitle,
              rejected
                ? formatFiscalEmissionErrorForUser(fiscalEmitLastError)
                : fiscalEmitLastError
                  ? formatFiscalEmissionErrorForUser(fiscalEmitLastError)
                  : FISCAL_SYSTEM_COPY.errors.pendingNoCae,
            );
          } else if (autoPrintEnabled) {
            void printSaleDoc(true)();
          }
          const hadCustomerPayment = normalizedPayments.some((d) => d.methodId === 'fiado');
          if (hadCustomerPayment && customerMethodAllowed && user?.id) {
            scheduleCustomerMaturityNudge(user.id, navigation);
          }
        }
        const firstMethod = paymentDrafts[0]?.methodId;
        if (firstMethod) {
          void setLastPosPaymentMethodId(firstMethod);
        }
      } catch (error: unknown) {
        // sale_already_voided: la venta ya está anulada en el server. Reintentar con el mismo
        // saleId siempre falla, así que descartamos el pendiente para que el próximo cobro
        // genere uno nuevo. Se sigue reportando (showCheckoutError) para visibilidad.
        if (error instanceof SaleAlreadyVoidedError) {
          pendingSaleIdRef.current = null;
        }
        console.warn('[pos.checkout] createSale failed:', (error as { message?: string })?.message);
        showCheckoutError(error, 'No se pudo registrar la venta.');
      } finally {
        setIsSaving(false);
      }
    },
    [
      activeCashSessionId,
      cart,
      clearCart,
      currentBranchId,
      paymentDrafts,
      resetCheckout,
      showSaleCompleted,
      saleFiscalMode,
      user?.id,
      cartEvaluation,
      discretionaryDiscountTotal,
      appliedPromoCodes,
      selectedCashierPromotionId,
      isOwner,
      currentBranch,
      promptPin,
      createKitchenTicketAfterSale,
      onAfterSaleRecorded,
      onCloseTableSession,
      shouldKeepTableSessionOpen,
      onAfterPartialTableSale,
      setLastMpQrSaleId,
      setLastMpQrAmount,
      setLastMpQrFiscalMode,
      setShowQrModal,
      setCheckoutVisible,
      runFiscalGate,
      chargeBlocked,
      promoUiStatus,
    ],
  );

  // ── Open checkout (inlined from usePosCheckoutOpener) ────────
  const open = useCallback(async () => {
    const freshCanEdit = canModuleAction(
      useSessionStore.getState().branches,
      useSessionStore.getState().currentBranchId ?? undefined,
      'POS',
      'edit'
    );
    if (!freshCanEdit) {
      showInfo('Modo solo lectura', 'Podés mirar el POS, no cobrar.');
      return;
    }
    if (chargeBlocked) {
      showInfo('Sin conexión', 'Cuando vuelva internet, cobrá con el mismo botón. El carrito queda.');
      return;
    }
    if (promoUiStatus?.hasStaleSelection) {
      showInfo('Promoción', promoUiStatus.staleMessage ?? STALE_PROMO_MESSAGE);
      return;
    }
    if (!currentBranchId) {
      return;
    }
    // Live store cart: after Dividir cuenta, replaceCart + beginCheckout run in the same tick.
    const liveCart = usePosStore.getState().cart;
    if (liveCart.length === 0) {
      showInfo('Carrito vacío', 'Agregá un ítem antes de cobrar.');
      return;
    }

    setCheckoutVisible(true);
    setSaleFiscalModeTouched(false);
    setSaleFiscalMode(
      allowFiscalSaleMode ? resolvePosSaleFiscalMode(defaultSaleFiscalMode, []) : 'non_fiscal'
    );
    setCheckoutLoading(true);
    try {
      if (customerMethodAllowed) {
        await pullCustomerDataWhenNeeded({ immediate: true }).catch(captureCatch('pos', 'checkout-pull-customer'));
      }
      const { methods, customers, customersTotal, activeCashSessionId: sessionId } = await loadOpenCheckoutContext({
        branchId: currentBranchId,
        customerMethodAllowed,
        mercadopagoQrMethodAllowed,
        checkMethodAllowed,
      });
      setPaymentMethods(methods);
      setCustomerOptions(customers);
      setCustomersTotal(customersTotal);
      setCustomerPageIndex(0);
      setActiveCashSessionId(sessionId);
      const lastMethodId = await getLastPosPaymentMethodId();
      const defaultMethod = pickDefaultPaymentMethodId(methods, lastMethodId, Boolean(sessionId));
      const openScale = getPosCheckoutMoneyScale();
      const { total: merchandiseTotal } = derivePosCheckoutTotals(
        liveCart,
        [],
        cartEvaluationRef.current,
        0,
        openScale,
      );
      const staffTipsEnabled = isPosStaffTipsEnabled(
        getOrganizationBusinessProfileForPos(),
      );
      usePosCheckoutStore.getState().setTipUiEnabled(staffTipsEnabled);
      if (!staffTipsEnabled) {
        usePosCheckoutStore.getState().setTipIntent({ amount: 0, percent: null, source: 'staff' });
      } else if (!usePosCheckoutStore.getState().tipIntent) {
        usePosCheckoutStore.getState().setTipIntent({ amount: 0, percent: null, source: 'staff' });
      }
      const tipAmount = resolvePosStaffTipAmount({
        tipIntentAmount: Number(usePosCheckoutStore.getState().tipIntent?.amount ?? 0),
        tipUiEnabled: usePosCheckoutStore.getState().tipUiEnabled,
        staffTipsEnabled,
      });
      const chargedTotal = merchandiseTotal + tipAmount;
      setPaymentDrafts([
        {
          id: `payment-${Date.now()}`,
          methodId: defaultMethod,
          amount: formatEditableMoneyAmount(chargedTotal),
        },
      ]);
    } catch (error: unknown) {
      pendingSaleIdRef.current = null;
      resetCheckout();
      // El Toast vive dentro del Modal Cobrar; diferir o se traga al cerrar.
      showCheckoutError(error, 'No se pudo preparar el cobro.', true);
    } finally {
      setCheckoutLoading(false);
    }
  }, [
    cart,
    currentBranchId,
    resetCheckout,
    setActiveCashSessionId,
    setCheckoutLoading,
    setCheckoutVisible,
    setCustomerOptions,
    setCustomerPageIndex,
    setPaymentDrafts,
    setPaymentMethods,
    setSaleFiscalMode,
    setSaleFiscalModeTouched,
    setCustomersTotal,
    mercadopagoQrMethodAllowed,
    customerMethodAllowed,
    checkMethodAllowed,
    allowFiscalSaleMode,
    defaultSaleFiscalMode,
    cartEvaluation,
    chargeBlocked,
    promoUiStatus,
    hasCommissions,
  ]);

  // ── Complete sale ────────────────────────────────────────────
  const complete = useCallback(async () => {
    // Ref guard síncrono: evita double-submit entre el check de isSaving (state)
    // y setIsSaving(true) dentro de runCreateSale (que tiene awaits previos).
    if (isSavingRef.current) { console.warn('[pos.checkout] swallow: double-submit while saving'); return; }
    isSavingRef.current = true;
    // #1636: el estado visible cubre TODA la transacción — validateCheckout
    // corre el force-sync del catálogo que en redes lentas tarda +30s. Sin
    // esto el botón quedaba habilitado y mudo y el cajero no sabía qué pasaba.
    setIsSaving(true);
    try {
      const result = await validateCheckout();
      if (result) {
        await runCreateSale(result.forceAllowSale, result.supervisorOverrideId);
      }
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [validateCheckout, runCreateSale]);

  // ── Cash management handler ──────────────────────────────────
  const handleOpenCashManagementFromCheckout = useCallback(() => {
    if (!canOpenCashManagement) {
      showInfo(
        'Sin acceso',
        `Pedí permiso de POS o de Caja para abrirla desde ${cashManagementNavHint}.`,
      );
      return;
    }
    setCheckoutVisible(false);
    navigateInShell(navigation, 'CashManagement', { returnToPos: true });
  }, [canOpenCashManagement, navigation, setCheckoutVisible]);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  return {
    isSaving,
    open,
    complete,
    sendCourseToKitchen,
    canSendCourseToKitchen,
    updateDraft,
    removeDraft,
    addSplitPayment,
    handleRequestStockCorrection,
    handleOpenCashManagementFromCheckout,
    clearPendingSaleId,
    ConfirmModal,
    CheckoutOverlayModal,
    SupervisorPinModal,
  };
}
